import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  House,
  Package,
  ArrowsClockwise,
  Stack,
  Users,
  SignOut,
  HardHat,
  ChartBar,
  Question,
} from "@phosphor-icons/react";
import BrandMark from "./BrandMark";
import TrialBanner from "./TrialBanner";

const navItems = [
  { to: "/dashboard", label: "Dashboard", icon: House, roles: ["admin", "warehouse_staff", "viewer"], end: true },
  { to: "/parts", label: "Parts", icon: Package, roles: ["admin", "warehouse_staff", "viewer"] },
  { to: "/transactions", label: "Transactions", icon: ArrowsClockwise, roles: ["admin", "warehouse_staff", "viewer"] },
  { to: "/reports", label: "Reports", icon: ChartBar, roles: ["admin", "warehouse_staff"], hideOnBasic: true },
  { to: "/categories", label: "Categories", icon: Stack, roles: ["admin"] },
  { to: "/users", label: "Users", icon: Users, roles: ["admin"] },
  { to: "/help", label: "Help", icon: Question, roles: ["admin", "warehouse_staff", "viewer"] },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const onLogout = async () => {
    await logout();
    navigate("/login");
  };

  const roleBadge =
    user?.role === "admin"
      ? "bg-[#1A2332] text-white"
      : user?.role === "warehouse_staff"
      ? "bg-amber-400 text-amber-950"
      : "bg-zinc-200 text-zinc-700";

  return (
    <div className="min-h-screen flex bg-zinc-50">
      {/* Sidebar */}
      <aside
        className="hidden md:flex md:flex-col w-64 bg-[#1A2332] text-zinc-100 border-r border-[#243044]"
        data-testid="sidebar"
      >
        <div className="px-6 py-6 border-b border-[#243044] flex items-center gap-3">
          <BrandMark size={44} className="flex-shrink-0 rounded-md" />
          <div>
            <div className="font-display text-lg font-bold leading-none">ezItemizer</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-1">
              Inventory Control
            </div>
          </div>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1">
          {navItems
            .filter((i) => i.roles.includes(user?.role))
            .filter((i) => !(i.hideOnBasic && user?.plan?.is_basic))
            .map(({ to, label, icon: Icon, end }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                data-testid={`nav-${label.toLowerCase()}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 text-sm transition-colors ${
                    isActive
                      ? "bg-[#FF9800] text-white"
                      : "text-zinc-400 hover:bg-[#1A2332] hover:text-zinc-100"
                  }`
                }
              >
                <Icon size={18} weight="duotone" />
                <span className="font-medium">{label}</span>
              </NavLink>
            ))}
        </nav>

        <div className="px-3 py-3 border-t border-[#243044]">
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="h-9 w-9 bg-zinc-800 flex items-center justify-center">
              <HardHat size={18} weight="duotone" className="text-amber-400" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{user?.name}</div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 truncate">
                {user?.email}
              </div>
            </div>
          </div>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 inset-x-0 z-40 bg-[#1A2332] text-zinc-100 border-b border-[#243044] flex items-center justify-between px-4 py-3">
        <div className="flex items-center gap-2 min-w-0">
          <BrandMark size={32} className="flex-shrink-0 rounded" />
          <div className="min-w-0">
            <div className="font-display text-base font-bold leading-none">ezItemizer</div>
            <div className="text-[9px] uppercase tracking-widest text-zinc-500 mt-0.5 truncate">
              {user?.email}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <NavLink
            to="/help"
            data-testid="help-mobile"
            title="Help"
            aria-label="Help"
            className="p-1.5 hover:text-[#FF9800] transition-colors"
          >
            <Question size={20} weight="bold" />
          </NavLink>
          <span
            className={`px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider ${roleBadge}`}
          >
            {user?.role?.replace("_", " ")}
          </span>
          <button
            onClick={onLogout}
            data-testid="logout-mobile"
            className="p-1.5 hover:bg-[#1A2332]"
            title="Sign out"
          >
            <SignOut size={16} weight="bold" />
          </button>
        </div>
      </div>

      {/* Mobile bottom nav — horizontally scrollable so all role-allowed items fit */}
      <div className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-[#1A2332] text-zinc-100 border-t border-[#243044] overflow-x-auto pb-[env(safe-area-inset-bottom)]">
        <div className="flex min-w-full">
          {navItems
            .filter((i) => i.roles.includes(user?.role))
            .filter((i) => !(i.hideOnBasic && user?.plan?.is_basic))
            .map(({ to, label, icon: Icon, end }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                data-testid={`mobile-nav-${label.toLowerCase()}`}
                className={({ isActive }) =>
                  `flex-1 min-w-[72px] flex flex-col items-center justify-center py-2 text-[9px] font-bold uppercase tracking-wider ${
                    isActive ? "text-[#FF9800] bg-[#1A2332]" : "text-zinc-500"
                  }`
                }
              >
                <Icon size={18} weight="duotone" />
                <span className="mt-1">{label}</span>
              </NavLink>
            ))}
        </div>
      </div>

      {/* Main */}
      <main className="flex-1 min-w-0 pt-14 md:pt-0 pb-20 md:pb-0">
        <TrialBanner />
        {/* Top bar */}
        <div className="hidden md:flex items-center justify-between border-b border-zinc-300 bg-white px-8 py-4">
          <div className="flex items-center gap-3">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">
              Operator
            </div>
            <div className="font-mono text-sm">{user?.email}</div>
            <span
              className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${roleBadge}`}
              data-testid="role-badge"
            >
              {user?.role?.replace("_", " ")}
            </span>
            <button
              onClick={onLogout}
              data-testid="logout-button"
              className="ml-2 inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] font-bold border border-zinc-300 hover:bg-[#1A2332] hover:text-white hover:border-[#1A2332] transition-colors"
            >
              <SignOut size={12} weight="bold" />
              Sign out
            </button>
          </div>
          <div className="flex items-center gap-4 text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">
            <span>System: <span className="text-emerald-600">ONLINE</span></span>
            <span>{new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</span>
          </div>
        </div>
        <div className="fade-up">{children}</div>
      </main>
    </div>
  );
}
